import { useContext, useEffect, useState, Fragment } from "react";
import { useSearchParams } from "react-router-dom";
import {
    Table, Checkbox, Button, Badge, Text, Group, Stack,
    Paper, ActionIcon, Tooltip, Modal, Textarea
} from "@mantine/core";
import { DatePickerInput } from '@mantine/dates'
import { showNotification } from '@mantine/notifications';
import { IconSend, IconAlertTriangle, IconPencil, IconCalendar } from "@tabler/icons-react";
import axios from "axios";
import moment from "moment";
import AuthContext from "../../../contexts/AuthContext";
import AppContext from "../../../contexts/AppContext/AppContext";

const REMINDER_BADGE = {
    "DUE_DATE": { label: "Vencimiento hoy", color: "orange" },
    "REMINDER": { label: "Recordatorio", color: "blue" },
};

// Mensajes base con placeholders
const REMINDER_MESSAGES_BASE = {
    "DUE_DATE": "Hola, te contactamos desde Administración FORVET. Te recordamos que hoy vence una de las cuotas de tu programa académico por un monto de MONTO. Si ya realizaste el pago, te agradecemos que nos envíes el comprobante para poder registrarlo correctamente. En caso de que aún esté pendiente, encontrarás a continuación los datos para efectuar la transferencia. Quedamos a disposición ante cualquier consulta. ¡Muchas gracias!",
    "REMINDER": "Hola, te contactamos desde Administración FORVET. Observamos que una de las cuotas de tu programa académico registra un vencimiento de DIAS_DE_ATRASO días por un monto de MONTO. Cuando realices el pago, te agradeceremos que nos envíes el comprobante para actualizar nuestros registros. A continuación encontrarás los datos para efectuar la transferencia. Si tenés alguna consulta o necesitás asistencia, no dudes en comunicarte con nosotros. Muchas gracias por tu atención.",
};


const MAX_SELECTED = 5;

function reminderId(r) {
    return `${r.paymentId}_${r.installmentId}_${r.reminderType}`;
}

export default function SalesReminders() {
    const { authToken } = useContext(AuthContext);
    const { bankAccountsData, dollarValue } = useContext(AppContext);
    const [searchParams] = useSearchParams();

    const getInitialDates = () => {
        const initDateParam = searchParams.get('initDate');
        const endDateParam = searchParams.get('endDate');

        if (initDateParam && endDateParam) {
            try {
                const initDate = moment(initDateParam, 'YYYY-MM-DD').toDate();
                const endDate = moment(endDateParam, 'YYYY-MM-DD').toDate();
                return { initDate, endDate };
            } catch (err) {
                console.warn("⚠️ Error parseando query params de fechas:", err);
            }
        }

        const today = new Date();
        const tenDaysAgo = new Date(today.getTime() - 10 * 24 * 60 * 60 * 1000);
        return { initDate: tenDaysAgo, endDate: today };
    };

    const { initDate: defaultInitDate, endDate: defaultEndDate } = getInitialDates();

    const [data, setData] = useState([]);
    const [selected, setSelected] = useState([]);
    const [loading, setLoading] = useState(true);
    const [notifying, setNotifying] = useState(false);
    const [filter, setFilter] = useState("ALL");

    const [initDate, setInitDate] = useState(defaultInitDate);
    const [endDate, setEndDate] = useState(defaultEndDate);

    const [customMessages, setCustomMessages] = useState({});
    const [editingId, setEditingId] = useState(null);

    const [modalOpen, setModalOpen] = useState(false);
    const [pendingItems, setPendingItems] = useState([]);

    useEffect(() => { fetchReminders(); }, [initDate, endDate, authToken]);

    async function fetchReminders() {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (initDate) {
                params.append("initDate", moment(initDate).format('YYYY-MM-DD'));
            }
            if (endDate) {
                params.append("endDate", moment(endDate).format('YYYY-MM-DD'));
            }

            const queryString = params.toString();
            const url = `${process.env.REACT_APP_BACKEND_SERVER}admin/administracion/pending-reminders${queryString ? `?${queryString}` : ''}`;

            const { data: reminders } = await axios.get(url, {
                headers: { Authorization: `Bearer ${authToken}` }
            });
            setData(reminders);
        } catch (err) {
            showNotification({
                color: "red",
                status: "error",
                title: "Error al cargar recordatorios",
                message: err.message || "No se pudieron obtener los recordatorios",
                autoClose: 5000,
            });
            console.error("Error fetching reminders:", err);
        } finally {
            setLoading(false);
        }
    }

    // ---- Calcular días de diferencia ----
    function calculateDaysDifference(dueDate) {
        const today = moment().startOf('day');
        const due = moment(dueDate).startOf('day');
        const daysDiff = due.diff(today, 'days');

        if (daysDiff === 0) return 0;
        if (daysDiff > 0) return daysDiff;
        return Math.abs(daysDiff);
    }

    // ---- Convertir moneda si es necesario ----
    function convertAmount(amount, installmentCurrency, paymentCurrency) {
        // Si la cuota está en USD y el payment está en ARS, convertir ARS a USD
        if (installmentCurrency === "USD" && paymentCurrency === "ARS") {
            return (amount * dollarValue).toFixed(2);
        }

        // Si ambas son iguales o si es otra combinación, retornar como está
        return amount.toFixed(2);
    }

    // ---- Formatear monto con símbolo de moneda ----
    function formatAmount(amount, currency) {
        const symbol = currency === "USD" ? "USD" : "ARS";
        return `${symbol} ${parseFloat(amount).toLocaleString("es-AR")}`;
    }


    // ---- Datos bancarios ----
    function getBankAccount(accountId) {
        const id = typeof accountId === "object" ? accountId?._id : accountId;
        if (!id) return null;
        const exist = bankAccountsData?.find(item => item._id === id);
        if (!exist) return null;
        return `\n\nLos datos de la cuenta a transferir son los siguientes:
Titular: ${exist.accountHolder}
CBU: ${exist.accountCBU}
Alias: ${exist.accountAlias}`;
    }

    // ---- Generar mensaje dinámico con días y monto ----
    function defaultMessage(r) {
        let baseMessage = REMINDER_MESSAGES_BASE[r.reminderType];

        // Calcular días
        const daysDiff = calculateDaysDifference(r.dueDate);

        // Convertir monto si es necesario
        const convertedAmount = convertAmount(r.agreedAmount, r.currency, r.paymentCurrency);
        const finalCurrency = r.paymentCurrency
        const formattedAmount = formatAmount(convertedAmount, finalCurrency);

        // Reemplazar placeholders
        if (r.reminderType === "REMINDER") {
            baseMessage = baseMessage.replace("DIAS_DE_ATRASO", daysDiff.toString());
        }
        baseMessage = baseMessage.replace("MONTO", formattedAmount);

        const bankData = getBankAccount(r.bankAccount);
        return bankData ? `${baseMessage}${bankData}` : baseMessage;
    }

    function getMessage(r) {
        const id = reminderId(r);
        return customMessages[id] ?? defaultMessage(r);
    }

    function setMessage(id, text) {
        setCustomMessages(prev => ({ ...prev, [id]: text }));
    }

    function resetMessage(id) {
        setCustomMessages(prev => {
            const next = { ...prev };
            delete next[id];
            return next;
        });
    }

    function requestNotify(items) {
        if (items.length > MAX_SELECTED) {
            showNotification({
                color: "orange",
                status: "warning",
                title: "Límite alcanzado.",
                message: `No puedes notificar más de ${MAX_SELECTED} destinatarios a la vez.`,
                autoClose: 3500,
            });
            return;
        }
        setPendingItems(items);
        setModalOpen(true);
    }

    async function confirmNotify() {
        setModalOpen(false);
        setNotifying(true);
        try {
            const reminders = pendingItems.map(r => ({
                paymentId: r.paymentId,
                installmentId: r.installmentId,
                reminderType: r.reminderType,
                text: getMessage(r)
            }));

            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_SERVER}admin/administracion/installments/reminder-sent`,
                { reminders },
                { headers: { Authorization: `Bearer ${authToken}` } }
            );

            const { results } = response.data;
            const succeeded = results.filter(r => r.ok).length;
            const failed = results.filter(r => !r.ok).length;

            if (failed === 0) {
                showNotification({
                    color: "green",
                    status: "success",
                    title: "¡Excelente! Operación exitosa.",
                    message: `${succeeded} notificación${succeeded > 1 ? "es" : ""} enviada${succeeded > 1 ? "s" : ""}`,
                });
            } else {
                showNotification({
                    color: "orange",
                    status: "warning",
                    title: "Operación parcial.",
                    message: `${succeeded} enviada${succeeded > 1 ? "s" : ""}, ${failed} falló${failed > 1 ? "ron" : ""}`,
                    autoClose: 5000,
                });

                if (failed > 0) {
                    const failedMessages = results
                        .filter(r => !r.ok)
                        .map((r, idx) => `${idx + 1}. ${r.message}`)
                        .join("\n");

                    showNotification({
                        color: "red",
                        status: "error",
                        title: "Detalles de errores:",
                        message: failedMessages,
                        autoClose: false,
                    });
                }
            }

            setSelected([]);
            setCustomMessages({});
            setEditingId(null);
        }
        catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Error al enviar notificaciones";
            showNotification({
                color: "red",
                status: "error",
                title: "Operación fallida.",
                message: `${errorMessage}`,
                autoClose: 5000,
            });
            console.error("Error en confirmNotify:", error);
        }
        finally {
            setNotifying(false);
            setPendingItems([]);
            await fetchReminders();
        }
    }

    function cancelNotify() {
        setModalOpen(false);
        setPendingItems([]);
    }

    // ---- Verificar si el pagador ya está seleccionado ----
    function isPayerAlreadySelected(payerEmail, currentId) {
        return selected.some(selectedId => {
            const selectedReminder = data.find(r => reminderId(r) === selectedId);
            const currentReminder = data.find(r => reminderId(r) === currentId);

            if (!selectedReminder || !currentReminder) return false;

            // Comparar por email del pagador
            return selectedReminder.payer.email === currentReminder.payer.email;
        });
    }

    function toggleRow(id) {
        setSelected(prev => {
            const isCurrentlySelected = prev.includes(id);
            const currentReminder = data.find(r => reminderId(r) === id);

            if (isCurrentlySelected) {
                const newSelected = prev.filter(x => x !== id);
                setEditingId(prevEditingId => (prevEditingId === id ? null : prevEditingId));
                return newSelected;
            }

            // ✅ NUEVA VALIDACIÓN: Verificar si el pagador ya está seleccionado
            if (isPayerAlreadySelected(currentReminder.payer.email, id)) {
                showNotification({
                    color: "orange",
                    status: "warning",
                    title: "Pagador duplicado.",
                    message: `${currentReminder.payer.name} ${currentReminder.payer.lastname} ya tiene una cuota seleccionada.`,
                    autoClose: 3000,
                });
                return prev;
            }

            if (prev.length >= MAX_SELECTED) {
                showNotification({
                    color: "orange",
                    status: "warning",
                    title: "Límite alcanzado.",
                    message: `No puedes seleccionar más de ${MAX_SELECTED} destinatarios.`,
                    autoClose: 3000,
                });
                return prev;
            }

            return [...prev, id];
        });
    }

    function toggleAll() {
        if (!data?.length) return;

        const allIds = filtered.map(reminderId);

        if (selected.length === filtered.length) {
            setSelected([]);
            setEditingId(null);
            return;
        }

        // ✅ NUEVA VALIDACIÓN: Filtrar duplicados por pagador
        const uniqueIds = allIds.filter((id, index) => {
            const currentReminder = data.find(r => reminderId(r) === id);
            const firstIndexOfPayer = allIds.findIndex(otherId => {
                const otherReminder = data.find(r => reminderId(r) === otherId);
                return otherReminder?.payer.email === currentReminder?.payer.email;
            });
            return index === firstIndexOfPayer;
        });

        if (uniqueIds.length > MAX_SELECTED) {
            showNotification({
                color: "orange",
                status: "warning",
                title: "Límite alcanzado.",
                message: `Solo se pueden seleccionar hasta ${MAX_SELECTED} destinatarios. Mostrando los primeros ${MAX_SELECTED}.`,
                autoClose: 3500,
            });
            setSelected(uniqueIds.slice(0, MAX_SELECTED));
            setEditingId(null);
            return;
        }

        setSelected(uniqueIds);
        if (uniqueIds.length < allIds.length) {
            showNotification({
                color: "blue",
                status: "info",
                title: "Duplicados removidos.",
                message: `Se removieron ${allIds.length - uniqueIds.length} cuota(s) de pagadores duplicados.`,
                autoClose: 3000,
            });
        }
    }

    function getSelectedReminders() {
        return data.filter(r => selected.includes(reminderId(r)));
    }

    function clearDateFilters() {
        const today = new Date();
        const tenDaysAgo = new Date(today.getTime() - 10 * 24 * 60 * 60 * 1000);
        setInitDate(tenDaysAgo);
        setEndDate(today);
    }

    const filtered = filter === "ALL"
        ? data
        : data.filter(r => r.reminderType === filter);

    const rows = filtered.map(row => {
        const id = reminderId(row);
        const badge = REMINDER_BADGE[row.reminderType];
        const isSel = selected.includes(id);
        const isCustom = customMessages[id] !== undefined;

        return (
            <Fragment key={id}>
                <Table.Tr bg={isSel ? "blue.0" : undefined}>
                    <Table.Td>
                        <Checkbox checked={isSel} onChange={() => toggleRow(id)} />
                    </Table.Td>
                    <Table.Td>
                        <Text fw={500} size="sm">{row.payer.name} {row.payer.lastname}</Text>
                        <Text size="xs" c="dimmed">{row.payer.email}</Text>
                    </Table.Td>
                    <Table.Td>
                        <Text size="sm" c="dimmed" truncate maw={180}>{row.description}</Text>
                    </Table.Td>
                    <Table.Td>
                        <Text size="sm" c="dimmed">{row.cycle}/{row.total_cycles}</Text>
                    </Table.Td>
                    <Table.Td>
                        <Text size="sm">{moment(row.dueDate).format("DD/MM/YYYY")}</Text>
                    </Table.Td>
                    <Table.Td>
                        <Text size="sm" ff="mono">
                            {formatAmount(convertAmount(row.agreedAmount, row.currency, row.paymentCurrency), row.paymentCurrency)}
                        </Text>
                    </Table.Td>
                    <Table.Td>
                        <Badge color={badge.color} variant="light" size="sm">{badge.label}</Badge>
                    </Table.Td>
                    <Table.Td>
                        <Tooltip label="Enviar notificación">
                            <ActionIcon
                                variant="subtle"
                                loading={notifying}
                                onClick={() => requestNotify([row])}
                            >
                                <IconSend size={16} />
                            </ActionIcon>
                        </Tooltip>
                    </Table.Td>
                </Table.Tr>

                {isSel && (
                    <Table.Tr bg="blue.0">
                        <Table.Td />
                        <Table.Td colSpan={7}>
                            {editingId === id ? (
                                <Stack gap="xs">
                                    <Textarea
                                        autosize
                                        minRows={2}
                                        maxRows={6}
                                        value={getMessage(row)}
                                        onChange={(e) => setMessage(id, e.currentTarget.value)}
                                    />
                                    <Group gap="xs">
                                        <Button
                                            size="xs"
                                            variant="light"
                                            disabled={getMessage(row).trim() === ""}
                                            onClick={() => setEditingId(null)}
                                        >
                                            Listo
                                        </Button>
                                        {isCustom && (
                                            <Button
                                                size="xs"
                                                variant="subtle"
                                                color="gray"
                                                onClick={() => { resetMessage(id); }}
                                            >
                                                Restaurar original
                                            </Button>
                                        )}
                                    </Group>
                                </Stack>
                            ) : (
                                <Button
                                    size="xs"
                                    variant="subtle"
                                    leftSection={<IconPencil size={14} />}
                                    onClick={() => setEditingId(id)}
                                >
                                    {isCustom ? "Mensaje personalizado — editar" : "Modificar mensaje"}
                                </Button>
                            )}
                        </Table.Td>
                    </Table.Tr>
                )}
            </Fragment>
        );
    });

    const isSingle = pendingItems.length === 1;
    const hasEmptyMessage = pendingItems.some(r => getMessage(r).trim() === "");

    return (
        <Stack mb={40} gap="md">

            <Modal
                opened={modalOpen}
                onClose={cancelNotify}
                title={
                    <Group gap="xs">
                        <IconAlertTriangle size={18} color="orange" />
                        <Text fw={500}>Confirmar notificación</Text>
                    </Group>
                }
                centered
            >
                <Stack gap="md">
                    <Text size="sm">
                        Vas a enviar{" "}
                        <strong>{pendingItems.length} mensaje{pendingItems.length > 1 ? "s" : ""}</strong>{" "}
                        por WhatsApp. Esta acción no se puede deshacer.
                    </Text>

                    {isSingle ? (
                        <Stack gap={6}>
                            <Text size="xs" c="dimmed">
                                {pendingItems[0].payer.name} {pendingItems[0].payer.lastname} — cuota{" "}
                                {pendingItems[0].cycle}/{pendingItems[0].total_cycles} (
                                {REMINDER_BADGE[pendingItems[0].reminderType].label})
                            </Text>
                            <Text size="sm">Editá el mensaje antes de enviarlo:</Text>
                            <Textarea
                                autosize
                                minRows={3}
                                maxRows={8}
                                value={getMessage(pendingItems[0])}
                                onChange={(e) =>
                                    setMessage(reminderId(pendingItems[0]), e.currentTarget.value)
                                }
                            />
                            {customMessages[reminderId(pendingItems[0])] !== undefined && (
                                <Group justify="flex-start">
                                    <Button
                                        size="xs"
                                        variant="subtle"
                                        color="gray"
                                        onClick={() => resetMessage(reminderId(pendingItems[0]))}
                                    >
                                        Restaurar original
                                    </Button>
                                </Group>
                            )}
                        </Stack>
                    ) : (
                        pendingItems.length <= 5 && (
                            <Stack gap={4}>
                                {pendingItems.map(r => (
                                    <Text key={reminderId(r)} size="xs" c="dimmed">
                                        · {r.payer.name} {r.payer.lastname} — cuota {r.cycle}/{r.total_cycles} ({REMINDER_BADGE[r.reminderType].label})
                                        {customMessages[reminderId(r)] !== undefined && " · editado"}
                                        {getBankAccount(r.bankAccount) && " · con datos bancarios"}
                                    </Text>
                                ))}
                            </Stack>
                        )
                    )}

                    <Group justify="flex-end" gap="sm">
                        <Button variant="default" onClick={cancelNotify}>Cancelar</Button>
                        <Button
                            color="red"
                            leftSection={<IconSend size={15} />}
                            loading={notifying}
                            disabled={hasEmptyMessage}
                            onClick={confirmNotify}
                        >
                            Confirmar y enviar
                        </Button>
                    </Group>
                </Stack>
            </Modal>

            <Group justify="space-between">
                <Group gap="xs">
                    <Text fw={500} size="lg">Recordatorios pendientes</Text>
                    <Badge color="red" variant="light">{data.length}</Badge>
                </Group>
                {selected.length > 0 && (
                    <Text size="sm" c="dimmed">
                        {selected.length}/{MAX_SELECTED} seleccionada{selected.length > 1 ? "s" : ""}
                    </Text>
                )}
            </Group>

            <Stack gap="sm">
                <Text size="sm" fw={500}>Filtrar por fecha de vencimiento</Text>
                <Group gap="md">
                    <DatePickerInput
                        label="Desde"
                        placeholder="Fecha inicio"
                        value={initDate}
                        onChange={setInitDate}
                        leftSection={<IconCalendar size={16} />}
                        clearable
                        maxDate={endDate}
                    />
                    <DatePickerInput
                        label="Hasta"
                        placeholder="Fecha fin"
                        value={endDate}
                        onChange={setEndDate}
                        leftSection={<IconCalendar size={16} />}
                        clearable
                        minDate={initDate}
                    />
                    <Button
                        size="xs"
                        variant="subtle"
                        color="gray"
                        onClick={clearDateFilters}
                    >
                        Restaurar por defecto
                    </Button>
                </Group>
            </Stack>

            <Group gap="xs">
                {[
                    { value: "ALL", label: "Todos", color: "gray" },
                    { value: "DUE_DATE", label: "Vencimiento hoy", color: "orange" },
                    { value: "REMINDER", label: "Recordatorios", color: "blue" },
                ].map(tab => (
                    <Badge
                        key={tab.value}
                        color={filter === tab.value ? tab.color : "gray"}
                        variant={filter === tab.value ? "filled" : "light"}
                        size="lg"
                        style={{ cursor: "pointer" }}
                        onClick={() => { setFilter(tab.value); setSelected([]); setEditingId(null); }}
                    >
                        {tab.label}{" "}
                        ({tab.value === "ALL"
                            ? data.length
                            : data.filter(r => r.reminderType === tab.value).length
                        })
                    </Badge>
                ))}
            </Group>

            <Group>
                <Button
                    leftSection={<IconSend size={15} />}
                    color="red"
                    disabled={selected.length === 0}
                    loading={notifying}
                    onClick={() => requestNotify(getSelectedReminders())}
                >
                    Notificar seleccionados
                </Button>
            </Group>

            <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
                <div style={{ overflowX: "auto" }}>
                    <Table highlightOnHover style={{ minWidth: 800 }}>
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th w={40}>
                                    <Checkbox
                                        checked={selected.length === filtered.length && filtered.length > 0}
                                        indeterminate={selected.length > 0 && selected.length < filtered.length}
                                        onChange={toggleAll}
                                    />
                                </Table.Th>
                                <Table.Th>Pagador</Table.Th>
                                <Table.Th>Descripción</Table.Th>
                                <Table.Th>Cuota</Table.Th>
                                <Table.Th>Vencimiento</Table.Th>
                                <Table.Th>Monto</Table.Th>
                                <Table.Th>Tipo</Table.Th>
                                <Table.Th />
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {loading ? (
                                <Table.Tr>
                                    <Table.Td colSpan={8} ta="center" py="xl">
                                        <Text c="dimmed" size="sm">Cargando...</Text>
                                    </Table.Td>
                                </Table.Tr>
                            ) : rows.length === 0 ? (
                                <Table.Tr>
                                    <Table.Td colSpan={8} ta="center" py="xl">
                                        <Text c="dimmed" size="sm">No hay recordatorios pendientes</Text>
                                    </Table.Td>
                                </Table.Tr>
                            ) : rows}
                        </Table.Tbody>
                    </Table>
                </div>
            </Paper>
        </Stack>
    );
}